/**
 * Project Cost Budget workbench — revisions, lines, EST3 convert, Excel import, Approval Runtime.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ApartmentOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
  CopyOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  LinkOutlined,
  PlusOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SaveOutlined,
  SendOutlined,
} from '@ant-design/icons';
import ReactDOM from 'react-dom/client';
import dayjs from 'dayjs';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { BudgetCodeSelect } from '@/components/project/BudgetCodeSelect';
import { PrintableProjectBudgetRevision } from '@/components/project/PrintableProjectBudgetRevision';
import { ProjectBudgetExcelImportWizard } from '@/components/project/ProjectBudgetExcelImportWizard';
import { ENTITY_TYPE_PROJECT_BUDGET_REVISION, ENTITY_TYPE_PROJECT_BUDGET_EXCEEDANCE } from '@/constants/approval-entity-types';
import { approvalService } from '@/services/approval.service';

import { useProject } from '@/hooks/useProjects';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { epcService } from '@/services/epc.service';
import {
  EST3_CONTRACT_SOURCE_PREFIX,
  est3SourceParams,
  projectService,
} from '@/services/project.service';
import type { Est3WbsNode } from '@/utils/est3WbsTree';
import { formatMoney } from '@/utils/format';

const { Title, Text } = Typography;

const TIP_STORAGE_KEY = 'budget-workbench-tip-dismissed';

function sourceDocumentPath(entityType: string, entityId: string): string | null {
  switch (String(entityType || '').toLowerCase()) {
    case 'purchase_order':
      return `/scm/procurement/purchase-orders/${entityId}`;
    case 'purchase_requisition':
      return `/scm/procurement/purchase-requests/${entityId}`;
    case 'supplier_invoice':
      return `/scm/procurement/supplier-invoices/${entityId}`;
    case 'project_timesheet':
      return `/prj/timesheets`;
    default:
      return null;
  }
}

function ExceedanceReviewSummary({
  exceedanceId,
  projectId,
  listRows,
}: {
  exceedanceId: string;
  projectId: string;
  listRows?: Record<string, unknown>[];
}) {
  const fromList = (listRows || []).find((r) => String(r.id) === exceedanceId);
  const detailQ = useQuery({
    queryKey: ['project-budget-exceedance', projectId, exceedanceId],
    queryFn: () => projectService.getBudgetExceedanceById(exceedanceId),
    enabled: !!exceedanceId && !fromList,
  });
  const row = fromList || detailQ.data;
  if (!row && detailQ.isLoading) {
    return <Text type="secondary">Loading exceedance…</Text>;
  }
  if (!row) return null;

  const reason = row.reason != null ? String(row.reason).trim() : '';
  const sourceType = String(row.source_entity_type || '');
  const sourceId = String(row.source_entity_id || '');
  const sourcePath = sourceType && sourceId ? sourceDocumentPath(sourceType, sourceId) : null;

  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message={`Over budget by ${formatMoney(Number(row.amount_over || 0))}`}
      description={
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text>
            <Text type="secondary">Reason: </Text>
            {reason || '—'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Requested {formatMoney(Number(row.amount_requested || 0))} · Available{' '}
            {formatMoney(Number(row.amount_available || 0))} · Source{' '}
            {sourceType.replace(/_/g, ' ') || '—'}
          </Text>
          {sourcePath ? (
            <Link to={sourcePath}>
              <Button size="small" type="link" icon={<LinkOutlined />} style={{ padding: 0 }}>
                Open source document
              </Button>
            </Link>
          ) : null}
        </Space>
      }
    />
  );
}

function apiErr(e: unknown, fallback: string) {
  const anyE = e as { response?: { data?: { message?: string } }; message?: string };
  return anyE?.response?.data?.message || anyE?.message || fallback;
}

function round2(n: number) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

const COST_CATEGORIES = ['Material', 'Labor', 'Subcontract', 'Overhead', 'Equipment'];

type BudgetLine = {
  key: string;
  cost_category: string;
  budgeted_quantity?: number;
  budgeted_unit_cost?: number;
  budgeted_amount: number;
  actual_amount?: number;
  committed_amount?: number;
  forecast_amount?: number;
  variance_amount?: number;
  notes?: string;
  wbs_node_id?: string | null;
  cbs_code_id?: string | null;
  mapping_key?: string | null;
};

function mapServerLines(lines: Record<string, unknown>[]): BudgetLine[] {
  return lines.map((l, i) => ({
    key: String(l.id || i),
    cost_category: String(l.cost_category || 'Material'),
    budgeted_quantity: Number(l.budgeted_quantity || 0),
    budgeted_unit_cost: Number(l.budgeted_unit_cost || 0),
    budgeted_amount: Number(l.budgeted_amount || 0),
    actual_amount: Number(l.actual_amount || 0),
    committed_amount: Number(l.committed_amount || 0),
    forecast_amount: Number(l.forecast_amount || 0),
    variance_amount:
      l.variance_amount != null
        ? Number(l.variance_amount)
        : Number(l.budgeted_amount || 0) - Number(l.actual_amount || 0),
    notes: l.notes != null ? String(l.notes) : '',
    wbs_node_id: (l.wbs_node_id as string) || null,
    cbs_code_id: (l.cbs_code_id as string) || null,
    mapping_key: (l.mapping_key as string) || null,
  }));
}

function linesFingerprint(lines: BudgetLine[]): string {
  return JSON.stringify(
    lines.map((l) => ({
      cost_category: l.cost_category,
      budgeted_quantity: Number(l.budgeted_quantity || 0),
      budgeted_unit_cost: Number(l.budgeted_unit_cost || 0),
      budgeted_amount: Number(l.budgeted_amount || 0),
      notes: l.notes || '',
      wbs_node_id: l.wbs_node_id || null,
      cbs_code_id: l.cbs_code_id || null,
    }))
  );
}

function validateBudgetLines(
  lines: BudgetLine[],
  opts: { structureReady: boolean }
): Map<string, string[]> {
  const issues = new Map<string, string[]>();
  for (const l of lines) {
    const errs: string[] = [];
    if (!COST_CATEGORIES.includes(l.cost_category)) {
      errs.push('Invalid category');
    }
    if (opts.structureReady && !l.wbs_node_id) {
      errs.push('WBS required');
    }
    const amount = Number(l.budgeted_amount || 0);
    const qtyRate = round2(Number(l.budgeted_quantity || 0) * Number(l.budgeted_unit_cost || 0));
    if (!(amount > 0 || qtyRate > 0)) {
      errs.push('Amount must be > 0');
    }
    if (errs.length) issues.set(l.key, errs);
  }
  return issues;
}

function roleColor(role: string) {
  if (role === 'ORIGINAL') return 'blue';
  if (role === 'CURRENT') return 'green';
  return 'default';
}

function statusColor(status: string) {
  if (status === 'Approved') return 'success';
  if (status === 'Submitted') return 'processing';
  if (status === 'Rejected') return 'error';
  if (status === 'Superseded') return 'default';
  return 'warning';
}

export const ProjectBudgetWorkbenchPage: React.FC = () => {
  const { id: projectId = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: project } = useProject(projectId);

  const selectedRevId = searchParams.get('revisionId') || '';
  const awardFromQuery = searchParams.get('awardId') || '';
  const exceedanceFromQuery = searchParams.get('exceedanceId') || '';

  const [awardId, setAwardId] = useState(awardFromQuery);
  const [editLines, setEditLines] = useState<BudgetLine[]>([]);
  const [savedFingerprint, setSavedFingerprint] = useState('');
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [seedCategories, setSeedCategories] = useState<string[]>(['Material']);
  const [est3PreviewEnabled, setEst3PreviewEnabled] = useState(false);
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);
  const [departmentId, setDepartmentId] = useState<string | undefined>();
  const [tipDismissed, setTipDismissed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(TIP_STORAGE_KEY) === '1'
  );

  useEffect(() => {
    if (project?.department_id) {
      setDepartmentId(project.department_id);
    }
  }, [project?.department_id]);

  const structureQ = useQuery({
    queryKey: ['project-budget-structure', projectId],
    queryFn: () => projectService.getBudgetStructureContext(projectId),
    enabled: !!projectId,
  });

  const wbsNodesForSelect: Est3WbsNode[] = useMemo(() => {
    const nodes = ((structureQ.data as any)?.nodes || []) as Record<string, unknown>[];
    return nodes.map((n) => ({
      id: String(n.id),
      code: String(n.code || ''),
      name: String(n.title || n.name || n.code || ''),
      parent_id: (n.parent_id as string) || null,
      sort_order: Number(n.sort_order || 0),
      is_active: n.is_active as number | boolean,
    }));
  }, [structureQ.data]);

  const cbsNodesForSelect: Est3WbsNode[] = useMemo(() => {
    const nodes = ((structureQ.data as any)?.cbs_codes || []) as Record<string, unknown>[];
    return nodes.map((n) => ({
      id: String(n.id),
      code: String(n.code || ''),
      name: String(n.name || n.code || ''),
      parent_id: (n.parent_id as string) || null,
      sort_order: Number(n.sort_order || 0),
      is_active: n.is_active as number | boolean,
    }));
  }, [structureQ.data]);

  const wbsLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of wbsNodesForSelect) {
      const code = String(n.code || '').trim();
      const name = String(n.name || '').trim();
      map.set(n.id, name && name !== code ? `${code} — ${name}` : code || n.id);
    }
    return map;
  }, [wbsNodesForSelect]);

  const cbsLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of cbsNodesForSelect) {
      const code = String(n.code || '').trim();
      const name = String(n.name || '').trim();
      map.set(n.id, name && name !== code ? `${code} — ${name}` : code || n.id);
    }
    return map;
  }, [cbsNodesForSelect]);

  const awardsQ = useQuery({
    queryKey: ['project-budget-est3-awards', projectId],
    queryFn: () => projectService.listEst3AwardsForBudget(projectId),
    enabled: !!projectId,
  });

  const contractsQ = useQuery({
    queryKey: ['project-budget-est3-contracts', projectId],
    queryFn: () => projectService.listEst3ContractsForBudget(projectId),
    enabled: !!projectId,
  });

  const awardOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: Array<{ value: string; label: string; award: Record<string, unknown> }> = [];
    for (const a of (awardsQ.data || []) as Record<string, unknown>[]) {
      const id = String(a.id || '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const num = String(a.award_number || a.id);
      const basis = String(a.award_basis_status || 'NOT_SET');
      const tender = String(a.tender_number || a.tender_title || '');
      const value =
        a.award_value != null
          ? ` · ${formatMoney(Number(a.award_value))} ${String(a.currency || '')}`.trim()
          : '';
      options.push({
        value: id,
        label: `${num} · ${String(a.status || '')} · basis ${basis}${tender ? ` · ${tender}` : ''}${value}`,
        award: a,
      });
    }
    for (const c of (contractsQ.data || []) as Record<string, unknown>[]) {
      const id = String(c.id || '');
      if (!id) continue;
      const value =
        c.contract_value != null
          ? ` · ${formatMoney(Number(c.contract_value))} ${String(c.currency || '')}`.trim()
          : '';
      options.push({
        value: `${EST3_CONTRACT_SOURCE_PREFIX}${id}`,
        label: `Contract ${String(c.contract_number || id)} · estimate ${String(c.basis_status || 'PINNED')}${value}`,
        award: { award_basis_status: c.basis_status || 'PINNED' },
      });
    }
    return options;
  }, [awardsQ.data, contractsQ.data]);

  useEffect(() => {
    if (awardId) return;
    if (awardFromQuery) {
      setAwardId(awardFromQuery);
      return;
    }
    if (awardOptions.length === 1) {
      setAwardId(awardOptions[0].value);
    } else {
      const pinned = awardOptions.find((o) =>
        ['PINNED', 'LOCKED'].includes(String(o.award.award_basis_status || ''))
      );
      if (pinned) setAwardId(pinned.value);
    }
  }, [awardOptions, awardFromQuery, awardId]);

  const revisionsQ = useQuery({
    queryKey: ['project-budget-revisions', projectId],
    queryFn: () => projectService.listBudgetRevisions(projectId),
    enabled: !!projectId,
  });

  const revisions = (revisionsQ.data || []) as Record<string, unknown>[];

  const effectiveRevId = useMemo(() => {
    if (selectedRevId) return selectedRevId;
    const draft = revisions.find((r) => ['Draft', 'Submitted', 'Rejected'].includes(String(r.status)));
    if (draft) return String(draft.id);
    const current = revisions.find((r) => String(r.budget_role) === 'CURRENT' && String(r.status) === 'Approved');
    if (current) return String(current.id);
    const original = revisions.find((r) => String(r.budget_role) === 'ORIGINAL');
    return original ? String(original.id) : '';
  }, [selectedRevId, revisions]);

  const detailQ = useQuery({
    queryKey: ['project-budget-revision', projectId, effectiveRevId],
    queryFn: () => projectService.getBudgetRevision(projectId, effectiveRevId),
    enabled: !!projectId && !!effectiveRevId,
  });

  const costSummaryQ = useQuery({
    queryKey: ['project-cost-control-summary', projectId],
    queryFn: () => projectService.getCostControlSummary(projectId),
    enabled: !!projectId,
    staleTime: 20_000,
  });

  const exceedancesQ = useQuery({
    queryKey: ['project-budget-exceedances', projectId],
    queryFn: () => projectService.listBudgetExceedances(projectId),
    enabled: !!projectId,
  });

  const lineageQ = useQuery({
    queryKey: ['epc-est3-budget-lineage', projectId],
    queryFn: () => projectService.getEst3BudgetLineage(projectId),
    enabled: !!projectId,
  });

  const previewQ = useQuery({
    queryKey: ['epc-est3-budget-preview', projectId, awardId],
    queryFn: () => projectService.getEst3BudgetPreview(projectId, awardId),
    enabled: !!projectId && !!awardId && est3PreviewEnabled,
    retry: false,
  });

  const revision = detailQ.data?.revision as Record<string, unknown> | undefined;
  const canEdit =
    !!revision &&
    ['Draft', 'Rejected'].includes(String(revision.status)) &&
    String(revision.budget_role) !== 'ORIGINAL';

  useEffect(() => {
    if (!selectedRevId && effectiveRevId) {
      const next = new URLSearchParams(searchParams);
      next.set('revisionId', effectiveRevId);
      setSearchParams(next, { replace: true });
    }
  }, [selectedRevId, effectiveRevId, searchParams, setSearchParams]);

  useEffect(() => {
    const lines = mapServerLines((detailQ.data?.lines || []) as Record<string, unknown>[]);
    setEditLines(lines);
    setSavedFingerprint(linesFingerprint(lines));
    setShowIssuesOnly(false);
  }, [detailQ.data]);

  const isDirty = canEdit && linesFingerprint(editLines) !== savedFingerprint;

  const structureReady = !!(structureQ.data as any)?.ready;
  const leafCount = Number((structureQ.data as any)?.leaf_count || 0);

  const lineIssues = useMemo(
    () => validateBudgetLines(editLines, { structureReady }),
    [editLines, structureReady]
  );
  const issueCount = lineIssues.size;

  const confirmIfDirty = (action: () => void, title = 'Discard unsaved changes?') => {
    if (!isDirty) {
      action();
      return;
    }
    Modal.confirm({
      title,
      content: 'You have unsaved budget line edits. Continue and lose those changes?',
      okText: 'Discard',
      okButtonProps: { danger: true },
      onOk: () => action(),
    });
  };

  const goToRevision = (revId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('revisionId', revId);
    setSearchParams(next);
  };

  const selectRevision = (revId: string) => {
    confirmIfDirty(() => goToRevision(revId), 'Switch revision and discard edits?');
  };

  const createM = useMutation({
    mutationFn: (mode: 'blank' | 'copy') => {
      if (!departmentId) {
        return Promise.reject(new Error('Department is required'));
      }
      return projectService.createBudgetRevision(projectId, {
        mode,
        changeReason: mode === 'copy' ? 'Copy from baseline' : 'From scratch',
        department_id: departmentId,
      });
    },
    onSuccess: (data) => {
      message.success('Draft revision created');
      qc.invalidateQueries({ queryKey: ['project-budget-revisions', projectId] });
      goToRevision(String(data.revision.id));
    },
    onError: (e: Error) => message.error(apiErr(e, 'Create failed')),
  });

  const seedFromWbsM = useMutation({
    mutationFn: (opts?: { revisionId?: string; categories?: string[] }) =>
      projectService.seedBudgetFromWbs(projectId, {
        revisionId: opts?.revisionId,
        leafOnly: true,
        categories: opts?.categories || seedCategories,
        replaceLines: true,
      }),
    onSuccess: (data) => {
      message.success(
        `Seeded ${(data.lines || []).length} line(s) from Project WBS control accounts`
      );
      qc.invalidateQueries({ queryKey: ['project-budget-revisions', projectId] });
      qc.invalidateQueries({ queryKey: ['project-budget-revision', projectId] });
      goToRevision(String(data.revision.id));
    },
    onError: (e: Error) => message.error(apiErr(e, 'Seed from WBS failed')),
  });

  const bootstrapWbsM = useMutation({
    mutationFn: () => epcService.bootstrapWbs(projectId),
    onSuccess: () => {
      message.success('Project WBS bootstrapped');
      qc.invalidateQueries({ queryKey: ['project-budget-structure', projectId] });
    },
    onError: (e: Error) => message.error(apiErr(e, 'WBS bootstrap failed')),
  });

  useEffect(() => {
    setEst3PreviewEnabled(false);
  }, [awardId]);

  const openSeedFromWbs = () => {
    const run = () => {
      const wbsReady = !!(structureQ.data as any)?.ready;
      if (!wbsReady) {
        Modal.confirm({
          title: 'Project WBS required',
          content:
            'Cost budgets for large projects must follow the Project WBS. Bootstrap an empty WBS structure first, then add nodes (or import), then seed the budget.',
          okText: 'Bootstrap WBS',
          onOk: () => bootstrapWbsM.mutateAsync(),
        });
        return;
      }
      Modal.confirm({
        title: 'Seed draft from Project WBS?',
        width: 520,
        content: (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              Creates one budget line per <strong>leaf WBS control account</strong> (
              {Number((structureQ.data as any)?.leaf_count || 0)} leaves) for each selected cost
              category. Enter amounts afterward, then submit for approval.
            </Text>
            <div>
              <Text type="secondary">Categories to seed</Text>
              <Select
                mode="multiple"
                style={{ width: '100%', marginTop: 8 }}
                value={seedCategories}
                onChange={setSeedCategories}
                options={COST_CATEGORIES.map((c) => ({ value: c, label: c }))}
              />
            </div>
          </Space>
        ),
        okText: 'Seed from WBS',
        onOk: () =>
          seedFromWbsM.mutateAsync({
            revisionId: canEdit ? effectiveRevId : undefined,
            categories: seedCategories.length ? seedCategories : ['Material'],
          }),
      });
    };
    confirmIfDirty(run, 'Seed from WBS and discard edits?');
  };

  const saveLinesM = useMutation({
    mutationFn: () =>
      projectService.replaceBudgetRevisionLines(
        projectId,
        effectiveRevId,
        editLines.map(({ key: _k, ...rest }) => rest)
      ),
    onSuccess: () => {
      message.success('Lines saved');
      setSavedFingerprint(linesFingerprint(editLines));
      qc.invalidateQueries({ queryKey: ['project-budget-revision', projectId, effectiveRevId] });
      qc.invalidateQueries({ queryKey: ['project-budget-revisions', projectId] });
    },
    onError: (e: Error) => message.error(apiErr(e, 'Save failed')),
  });

  const runSave = () => {
    if (!canEdit || !effectiveRevId) return;
    if (!editLines.length) {
      message.warning('Add at least one budget line before saving');
      return;
    }
    const issues = validateBudgetLines(editLines, { structureReady });
    if (issues.size) {
      setShowIssuesOnly(true);
      message.error(`Fix ${issues.size} line issue(s) before saving`);
      return;
    }
    saveLinesM.mutate();
  };

  const submitM = useMutation({
    mutationFn: () => projectService.submitBudgetRevision(projectId, effectiveRevId),
    onSuccess: () => {
      message.success('Submitted for approval');
      setSavedFingerprint(linesFingerprint(editLines));
      qc.invalidateQueries({ queryKey: ['project-budget-revisions', projectId] });
      qc.invalidateQueries({ queryKey: ['project-budget-revision', projectId, effectiveRevId] });
      qc.invalidateQueries({
        queryKey: ['approval-summary', ENTITY_TYPE_PROJECT_BUDGET_REVISION, effectiveRevId],
      });
    },
    onError: (e: Error) => message.error(apiErr(e, 'Submit failed')),
  });

  const runSubmit = () => {
    if (!canEdit || !effectiveRevId) return;
    if (isDirty) {
      Modal.confirm({
        title: 'Save before submit?',
        content: 'You have unsaved line edits. Save them first, then submit for approval.',
        okText: 'Save now',
        onOk: async () => {
          const issues = validateBudgetLines(editLines, { structureReady });
          if (!editLines.length || issues.size) {
            setShowIssuesOnly(true);
            message.error(
              !editLines.length
                ? 'Add at least one budget line before saving'
                : `Fix ${issues.size} line issue(s) before saving`
            );
            return;
          }
          await saveLinesM.mutateAsync();
          message.info('Saved — click Submit again when ready');
        },
      });
      return;
    }
    if (!editLines.length) {
      message.warning('Cannot submit an empty budget revision');
      return;
    }
    const issues = validateBudgetLines(editLines, { structureReady });
    if (issues.size) {
      setShowIssuesOnly(true);
      message.error(`Fix ${issues.size} line issue(s) before submit`);
      return;
    }
    Modal.confirm({
      title: 'Submit budget revision for approval?',
      content: 'This starts Approval Runtime. You will not be able to edit lines until rejected or a new draft is created.',
      okText: 'Submit',
      onOk: () => submitM.mutateAsync(),
    });
  };

  const openImportWizard = () => {
    confirmIfDirty(() => setImportWizardOpen(true), 'Open Excel import and discard edits?');
  };

  const convertM = useMutation({
    mutationFn: () =>
      projectService.convertBudgetFromEst3(projectId, {
        ...est3SourceParams(awardId),
        previewHash: String((previewQ.data as any)?.preview_hash || ''),
      }),
    onSuccess: (r: any) => {
      message.success(r?.idempotent ? 'Original Budget already exists' : 'Original Budget generated');
      qc.invalidateQueries({ queryKey: ['project-budget-revisions', projectId] });
      qc.invalidateQueries({ queryKey: ['epc-est3-budget-lineage', projectId] });
    },
    onError: (e: Error) => message.error(apiErr(e, 'Convert failed')),
  });

  const downloadTemplate = async () => {
    try {
      const blob = await projectService.downloadBudgetImportTemplate(projectId);
      if (!(blob instanceof Blob)) {
        throw new Error('Template download did not return a file');
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'project-cost-budget-import-workbook.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(e?.message || 'Template download failed');
    }
  };

  const exportRevision = async () => {
    if (!effectiveRevId) {
      message.warning('Select a revision to export');
      return;
    }
    try {
      const blob = await projectService.exportBudgetRevision(projectId, effectiveRevId);
      if (!(blob instanceof Blob)) throw new Error('Export did not return a file');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `project-cost-budget-${effectiveRevId.slice(0, 8)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(e?.message || 'Export failed');
    }
  };

  const lineTotal = editLines.reduce((s, l) => s + (Number(l.budgeted_amount) || 0), 0);

  const printRevisionDetail = async () => {
    if (!revision || !effectiveRevId) {
      message.warning('Select a revision to print');
      return;
    }
    let approvalSigners: Awaited<ReturnType<typeof approvalService.getPrintSigners>> = [];
    if (revision.approval_instance_id) {
      try {
        approvalSigners = await approvalService.getPrintSigners(
          ENTITY_TYPE_PROJECT_BUDGET_REVISION,
          effectiveRevId
        );
      } catch {
        approvalSigners = [];
      }
    }

    const projectNumber = String(
      (project as any)?.project_number || (project as any)?.project_code || projectId
    );
    const projectName = String((project as any)?.project_name || '');

    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };

    root.render(
      <PrintableProjectBudgetRevision
        projectNumber={projectNumber}
        projectName={projectName}
        revision={{
          id: effectiveRevId,
          revision_number: Number(revision.revision_number || 1),
          budget_role: String(revision.budget_role || 'DRAFT'),
          status: String(revision.status || 'Draft'),
          source_type: revision.source_type != null ? String(revision.source_type) : null,
          change_reason: revision.change_reason != null ? String(revision.change_reason) : null,
          total_budgeted_amount:
            revision.total_budgeted_amount != null
              ? Number(revision.total_budgeted_amount)
              : lineTotal,
          line_count: editLines.length,
          currency: revision.currency != null ? String(revision.currency) : 'AED',
          approval_instance_id:
            revision.approval_instance_id != null ? String(revision.approval_instance_id) : null,
          submitted_at: revision.submitted_at != null ? String(revision.submitted_at) : null,
          approved_at: revision.approved_at != null ? String(revision.approved_at) : null,
        }}
        lines={editLines.map((l) => ({
          wbs_label: l.wbs_node_id ? wbsLabelById.get(l.wbs_node_id) || undefined : undefined,
          cbs_label: l.cbs_code_id ? cbsLabelById.get(l.cbs_code_id) || undefined : undefined,
          cost_category: l.cost_category,
          budgeted_quantity: l.budgeted_quantity,
          budgeted_unit_cost: l.budgeted_unit_cost,
          budgeted_amount: l.budgeted_amount,
          actual_amount: l.actual_amount,
          committed_amount: l.committed_amount,
          variance_amount: l.variance_amount,
          notes: l.notes,
        }))}
        approvalSigners={approvalSigners}
        printedAt={dayjs().format('DD-MMM-YYYY HH:mm')}
        onClose={cleanup}
      />
    );
    window.addEventListener('afterprint', cleanup);
  };

  const wbsRollups = useMemo(() => {
    const map = new Map<string, { wbs: string; amount: number; lines: number }>();
    for (const l of editLines) {
      const id = l.wbs_node_id || '';
      const label = id ? wbsLabelById.get(id) || id : '(Unassigned WBS)';
      const cur = map.get(label) || { wbs: label, amount: 0, lines: 0 };
      cur.amount += Number(l.budgeted_amount) || 0;
      cur.lines += 1;
      map.set(label, cur);
    }
    return [...map.values()].sort((a, b) => a.wbs.localeCompare(b.wbs));
  }, [editLines, wbsLabelById]);

  const summaryActive = !!(costSummaryQ.data as any)?.active;
  const actualTotal = summaryActive
    ? Number((costSummaryQ.data as any)?.actual) || 0
    : editLines.reduce((s, l) => s + (Number(l.actual_amount) || 0), 0);
  const committedTotal = summaryActive
    ? Number((costSummaryQ.data as any)?.committed) || 0
    : editLines.reduce((s, l) => s + (Number(l.committed_amount) || 0), 0);
  const varianceTotal = summaryActive
    ? Number((costSummaryQ.data as any)?.variance) || 0
    : editLines.reduce((s, l) => {
        const v =
          l.variance_amount != null
            ? Number(l.variance_amount)
            : Number(l.budgeted_amount || 0) - Number(l.actual_amount || 0);
        return s + v;
      }, 0);

  const displayLines = useMemo(
    () => (showIssuesOnly ? editLines.filter((l) => lineIssues.has(l.key)) : editLines),
    [editLines, showIssuesOnly, lineIssues]
  );

  const dismissTip = () => {
    setTipDismissed(true);
    try {
      localStorage.setItem(TIP_STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const runEst3Preview = () => {
    if (!awardId) return;
    if (est3PreviewEnabled) {
      void previewQ.refetch();
    } else {
      setEst3PreviewEnabled(true);
    }
  };

  const addBlankLine = () =>
    setEditLines((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        cost_category: 'Material',
        budgeted_quantity: 0,
        budgeted_unit_cost: 0,
        budgeted_amount: 0,
        notes: '',
        wbs_node_id: null,
        cbs_code_id: null,
      },
    ]);

  const stickyBar = (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        background: 'var(--ant-color-bg-container, #fff)',
        padding: '8px 0',
        marginBottom: 8,
        borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
      }}
    >
      <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space wrap>
          {isDirty && <Tag color="orange">Unsaved changes</Tag>}
          {issueCount > 0 && canEdit && (
            <Tag
              color="error"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowIssuesOnly((v) => !v)}
            >
              {issueCount} issue{issueCount === 1 ? '' : 's'}
              {showIssuesOnly ? ' · show all' : ' · filter'}
            </Tag>
          )}
          {revision?.source_type != null && <Tag>{String(revision.source_type)}</Tag>}
        </Space>
        <Space wrap>
          <Button
            icon={<PrinterOutlined />}
            disabled={!effectiveRevId}
            onClick={() => void printRevisionDetail()}
          >
            Print
          </Button>
          {canEdit && (
            <>
              <Button icon={<SaveOutlined />} onClick={runSave} loading={saveLinesM.isPending}>
                Save
              </Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={runSubmit}
                loading={submitM.isPending}
              >
                Submit
              </Button>
            </>
          )}
        </Space>
      </Space>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/prj/projects/${projectId}/edit`)}
            >
              Project
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              Project Cost Budget
            </Title>
          </Space>
          <Space wrap>
            <Text type="secondary">
              {String((project as any)?.project_number || (project as any)?.project_code || '')} ·{' '}
              {String((project as any)?.project_name || '')}
            </Text>
            <Button
              type="link"
              icon={<LinkOutlined />}
              onClick={() => navigate(`/epc?tab=wbs&projectId=${encodeURIComponent(projectId)}`)}
            >
              EPC Hub WBS
            </Button>
            <Button type="link" onClick={() => navigate(`/prj/projects/${projectId}/budgets/variance`)}>
              Budget vs Actual
            </Button>
          </Space>
        </Space>

        {!tipDismissed && (
          <Alert
            type="info"
            showIcon
            closable
            onClose={dismissTip}
            message="Tip"
            description="Original Budget is the first cost baseline (often EST3). Current Budget is the approved evolved revision. Prefer WBS leaf control accounts for cost control."
          />
        )}

        {structureQ.isError && (
          <Alert
            type="error"
            showIcon
            message="Could not load Project WBS structure"
            description={apiErr(structureQ.error, 'Structure request failed')}
            action={
              <Button size="small" onClick={() => structureQ.refetch()}>
                Retry
              </Button>
            }
          />
        )}

        {!structureQ.isLoading && !structureQ.isError && !structureReady && (
          <Alert
            type="warning"
            showIcon
            message="Project WBS is empty"
            description="Bootstrap WBS or import Excel (missing codes are created on commit). Maintain the tree in EPC Hub → WBS."
            action={
              <Space wrap>
                <Button
                  size="small"
                  type="primary"
                  loading={bootstrapWbsM.isPending}
                  onClick={() => bootstrapWbsM.mutate()}
                >
                  Bootstrap WBS
                </Button>
                <Button size="small" icon={<FileExcelOutlined />} onClick={openImportWizard}>
                  Import Excel
                </Button>
              </Space>
            }
          />
        )}

        <Card size="small">
          <Row gutter={[16, 8]}>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Lines" value={editLines.length} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Budget" value={lineTotal} formatter={(v) => formatMoney(Number(v))} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Actual" value={actualTotal} formatter={(v) => formatMoney(Number(v))} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Committed"
                value={committedTotal}
                formatter={(v) => formatMoney(Number(v))}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Variance"
                value={varianceTotal}
                valueStyle={varianceTotal < 0 ? { color: '#cf1322' } : undefined}
                formatter={(v) => formatMoney(Number(v))}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Revision
                </Text>
                <div style={{ marginTop: 4 }}>
                  {revision ? (
                    <Space size={4} wrap>
                      <Tag color={roleColor(String(revision.budget_role))}>
                        {String(revision.budget_role)}
                      </Tag>
                      <Tag color={statusColor(String(revision.status))}>
                        {String(revision.status)}
                      </Tag>
                    </Space>
                  ) : (
                    <Text type="secondary">—</Text>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        <Row gutter={16}>
          <Col xs={24} lg={7}>
            <Card
              size="small"
              title="Revisions"
              extra={
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => revisionsQ.refetch()}
                  loading={revisionsQ.isFetching}
                />
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Department
                  </Text>
                  <DepartmentSelect
                    style={{ width: '100%' }}
                    value={departmentId}
                    onChange={(v) => setDepartmentId(v)}
                    placeholder="Select department"
                  />
                </div>
                <Space wrap>
                  <Button
                    size="small"
                    type="primary"
                    icon={<ApartmentOutlined />}
                    loading={seedFromWbsM.isPending}
                    onClick={openSeedFromWbs}
                  >
                    From WBS
                  </Button>
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    loading={createM.isPending}
                    onClick={() => {
                      if (!departmentId) {
                        message.error('Department is required');
                        return;
                      }
                      confirmIfDirty(() => createM.mutate('blank'));
                    }}
                  >
                    Empty draft
                  </Button>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    loading={createM.isPending}
                    onClick={() => {
                      if (!departmentId) {
                        message.error('Department is required');
                        return;
                      }
                      confirmIfDirty(() => createM.mutate('copy'));
                    }}
                  >
                    Copy baseline
                  </Button>
                </Space>
                <Table
                  size="small"
                  rowKey={(r) => String(r.id)}
                  pagination={false}
                  loading={revisionsQ.isLoading}
                  dataSource={revisions}
                  locale={{ emptyText: 'No revisions yet' }}
                  onRow={(r) => ({
                    onClick: () => selectRevision(String(r.id)),
                    style: {
                      cursor: 'pointer',
                      background:
                        String(r.id) === effectiveRevId ? 'rgba(22,119,255,0.08)' : undefined,
                    },
                  })}
                  columns={[
                    { title: 'Rev', dataIndex: 'revision_number', width: 48 },
                    {
                      title: 'Role',
                      dataIndex: 'budget_role',
                      render: (v: string) => <Tag color={roleColor(v)}>{v}</Tag>,
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      render: (v: string) => <Tag color={statusColor(v)}>{v}</Tag>,
                    },
                    {
                      title: 'Total',
                      dataIndex: 'total_budgeted_amount',
                      align: 'right' as const,
                      render: (v: number) => formatMoney(Number(v || 0)),
                    },
                  ]}
                />
              </Space>
            </Card>

            <Collapse
              style={{ marginTop: 16 }}
              defaultActiveKey={[]}
              items={[
                {
                  key: 'tools',
                  label: 'More tools',
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      <div>
                        <Text strong>From EST3 estimate (award basis or contract)</Text>
                        <Text type="secondary" style={{ display: 'block' }}>
                          No estimate? Create a revision manually or from Excel — when the project
                          has no Original Budget yet, the first approved manual / Excel revision
                          becomes the Original Budget.
                        </Text>
                        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                          <Select
                            showSearch
                            allowClear
                            placeholder={awardsQ.isLoading ? 'Loading awards…' : 'Select award or contract'}
                            value={awardId || undefined}
                            onChange={(v) => setAwardId(v || '')}
                            options={awardOptions}
                            loading={awardsQ.isLoading}
                            optionFilterProp="label"
                            style={{ width: '100%' }}
                            notFoundContent={
                              awardsQ.isError
                                ? 'Failed to load awards'
                                : 'No awards found for this project'
                            }
                          />
                          <Button
                            disabled={!awardId}
                            onClick={runEst3Preview}
                            loading={previewQ.isFetching}
                          >
                            Preview convert
                          </Button>
                          {lineageQ.data && (lineageQ.data as any).conversion && (
                            <Alert
                              type="success"
                              showIcon
                              message={`Original exists · ${formatMoney(Number((lineageQ.data as any).original_budget_total || 0))}`}
                            />
                          )}
                          {est3PreviewEnabled && previewQ.isError && (
                            <Alert
                              type="error"
                              showIcon
                              message={apiErr(previewQ.error, 'Preview failed')}
                            />
                          )}
                          {previewQ.data && (
                            <>
                              <Tag
                                color={
                                  (previewQ.data as any).status === 'READY' ? 'green' : 'red'
                                }
                              >
                                {(previewQ.data as any).status}
                              </Tag>
                              <Text>
                                Mapped{' '}
                                {formatMoney(Number((previewQ.data as any).mapped_total || 0))} ·{' '}
                                {(previewQ.data as any).row_count} rows
                              </Text>
                              <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                disabled={
                                  (previewQ.data as any).status !== 'READY' ||
                                  !!(lineageQ.data as any)?.conversion
                                }
                                loading={convertM.isPending}
                                onClick={() =>
                                  Modal.confirm({
                                    title: 'Generate Original Project Cost Budget?',
                                    content:
                                      'Cost-only from Award Basis. Idempotent; locks Award Basis.',
                                    onOk: () => convertM.mutateAsync(),
                                  })
                                }
                              >
                                Generate Original
                              </Button>
                            </>
                          )}
                        </Space>
                      </div>

                      <div>
                        <Text strong>Excel import / export</Text>
                        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
                            Download import workbook
                          </Button>
                          <Button
                            icon={<DownloadOutlined />}
                            disabled={!effectiveRevId}
                            onClick={exportRevision}
                          >
                            Export revision
                          </Button>
                          <Button
                            type="primary"
                            icon={<FileExcelOutlined />}
                            onClick={openImportWizard}
                          >
                            Import Excel wizard
                          </Button>
                        </Space>
                      </div>

                      <div>
                        <Text strong>Budget exceedances</Text>
                        <Table
                          size="small"
                          style={{ marginTop: 8 }}
                          rowKey={(r) => String(r.id)}
                          pagination={false}
                          loading={exceedancesQ.isLoading}
                          dataSource={(exceedancesQ.data || []).slice(0, 8)}
                          locale={{ emptyText: 'No exceedance requests' }}
                          columns={[
                            {
                              title: 'Status',
                              dataIndex: 'status',
                              width: 110,
                              render: (v: string) => <Tag>{v}</Tag>,
                            },
                            {
                              title: 'Over',
                              dataIndex: 'amount_over',
                              align: 'right' as const,
                              width: 110,
                              render: (v: number) => formatMoney(Number(v || 0)),
                            },
                            {
                              title: 'Reason',
                              dataIndex: 'reason',
                              ellipsis: true,
                              render: (v: unknown) =>
                                v ? (
                                  <Text type="secondary" ellipsis={{ tooltip: String(v) }}>
                                    {String(v)}
                                  </Text>
                                ) : (
                                  '—'
                                ),
                            },
                            {
                              title: 'Source',
                              key: 'src',
                              width: 120,
                              render: (_: unknown, r: Record<string, unknown>) =>
                                `${String(r.source_entity_type || '')}`.replace(/_/g, ' '),
                            },
                          ]}
                          onRow={(r) => ({
                            onClick: () => {
                              const next = new URLSearchParams(searchParams);
                              next.set('exceedanceId', String(r.id));
                              setSearchParams(next);
                            },
                            style: { cursor: 'pointer' },
                          })}
                        />
                      </div>

                      <div>
                        <Text strong>WBS health</Text>
                        <div style={{ marginTop: 8 }}>
                          {structureQ.isError ? (
                            <Text type="danger">Structure failed to load</Text>
                          ) : structureReady ? (
                            <Text type="secondary">
                              {Number((structureQ.data as any)?.node_count || 0)} nodes · {leafCount}{' '}
                              leaf accounts ·{' '}
                              {Number((structureQ.data as any)?.cbs_codes?.length || 0)} CBS codes
                            </Text>
                          ) : (
                            <Space>
                              <Text type="secondary">Not ready</Text>
                              <Button
                                size="small"
                                loading={bootstrapWbsM.isPending}
                                onClick={() => bootstrapWbsM.mutate()}
                              >
                                Bootstrap
                              </Button>
                            </Space>
                          )}
                        </div>
                      </div>
                    </Space>
                  ),
                },
              ]}
            />
          </Col>

          <Col xs={24} lg={17}>
            <Card
              size="small"
              title={
                revision
                  ? `Revision ${revision.revision_number} · ${revision.budget_role} · ${revision.status}`
                  : 'Select or create a revision'
              }
            >
              {stickyBar}

              {exceedanceFromQuery && (
                <Card size="small" type="inner" title="Budget Exceedance" style={{ marginBottom: 12 }}>
                  <ExceedanceReviewSummary
                    exceedanceId={exceedanceFromQuery}
                    projectId={projectId}
                    listRows={exceedancesQ.data}
                  />
                  <ApprovalPanel
                    entityType={ENTITY_TYPE_PROJECT_BUDGET_EXCEEDANCE}
                    entityId={exceedanceFromQuery}
                    extraInvalidateQueryKeys={[['project-budget-exceedances', projectId]]}
                  />
                </Card>
              )}

              {!effectiveRevId && (
                <Card size="small" type="inner" style={{ textAlign: 'center', padding: 16 }}>
                  <Title level={5}>Create your first cost budget</Title>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Seed from Project WBS, import Excel, or start an empty draft.
                  </Text>
                  <Space wrap>
                    <Button
                      type="primary"
                      icon={<ApartmentOutlined />}
                      loading={seedFromWbsM.isPending}
                      onClick={openSeedFromWbs}
                    >
                      From WBS
                    </Button>
                    <Button icon={<FileExcelOutlined />} onClick={openImportWizard}>
                      Import Excel
                    </Button>
                    <Button
                      icon={<PlusOutlined />}
                      loading={createM.isPending}
                      onClick={() => {
                        if (!departmentId) {
                          message.error('Department is required');
                          return;
                        }
                        createM.mutate('blank');
                      }}
                    >
                      Empty draft
                    </Button>
                  </Space>
                </Card>
              )}

              {effectiveRevId && (
                <>
                  {canEdit && issueCount > 0 && (
                    <Alert
                      type="error"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={`${issueCount} line(s) need attention before save/submit`}
                      description="Every line needs a valid category, amount > 0, and WBS when the project structure is ready."
                      action={
                        <Button size="small" onClick={() => setShowIssuesOnly(true)}>
                          Show issues
                        </Button>
                      }
                    />
                  )}

                  {canEdit && (
                    <Space wrap style={{ marginBottom: 8 }}>
                      <Button
                        size="small"
                        icon={<ApartmentOutlined />}
                        loading={seedFromWbsM.isPending}
                        onClick={openSeedFromWbs}
                      >
                        Re-seed from WBS
                      </Button>
                      <Button size="small" icon={<PlusOutlined />} onClick={addBlankLine}>
                        Add line
                      </Button>
                    </Space>
                  )}

                  {wbsRollups.length > 0 && (
                    <Collapse
                      style={{ marginBottom: 12 }}
                      defaultActiveKey={wbsRollups.length > 8 ? [] : ['rollup']}
                      items={[
                        {
                          key: 'rollup',
                          label: `WBS roll-up (${wbsRollups.length})`,
                          children: (
                            <Table
                              size="small"
                              pagination={false}
                              rowKey="wbs"
                              dataSource={wbsRollups}
                              columns={[
                                { title: 'WBS', dataIndex: 'wbs' },
                                {
                                  title: 'Lines',
                                  dataIndex: 'lines',
                                  width: 70,
                                  align: 'right' as const,
                                },
                                {
                                  title: 'Amount',
                                  dataIndex: 'amount',
                                  width: 120,
                                  align: 'right' as const,
                                  render: (v: number) => formatMoney(v),
                                },
                              ]}
                            />
                          ),
                        },
                      ]}
                    />
                  )}

                  <Table
                    size="small"
                    rowKey="key"
                    pagination={{ pageSize: 20 }}
                    dataSource={displayLines}
                    scroll={{ x: 1100 }}
                    rowClassName={(row) =>
                      lineIssues.has(row.key) ? 'budget-line-issue-row' : ''
                    }
                    onRow={(row) => {
                      const errs = lineIssues.get(row.key);
                      return errs
                        ? {
                            title: errs.join(' · '),
                            style: { background: 'rgba(255, 77, 79, 0.06)' },
                          }
                        : {};
                    }}
                    columns={[
                      {
                        title: 'WBS',
                        dataIndex: 'wbs_node_id',
                        width: 280,
                        render: (v: string | null | undefined, row: BudgetLine) =>
                          canEdit ? (
                            <BudgetCodeSelect
                              size="small"
                              kind="wbs"
                              nodes={wbsNodesForSelect}
                              value={v || undefined}
                              placeholder={structureReady ? 'Search WBS…' : 'No WBS'}
                              disabled={!structureReady}
                              status={
                                lineIssues.get(row.key)?.includes('WBS required')
                                  ? 'error'
                                  : undefined
                              }
                              onChange={(val) =>
                                setEditLines((prev) =>
                                  prev.map((l) =>
                                    l.key === row.key
                                      ? { ...l, wbs_node_id: (val as string) || null }
                                      : l
                                  )
                                )
                              }
                            />
                          ) : (
                            (v && wbsLabelById.get(v)) || '—'
                          ),
                      },
                      {
                        title: 'CBS',
                        dataIndex: 'cbs_code_id',
                        width: 260,
                        render: (v: string | null | undefined, row: BudgetLine) =>
                          canEdit ? (
                            <BudgetCodeSelect
                              size="small"
                              kind="cbs"
                              nodes={cbsNodesForSelect}
                              value={v || undefined}
                              placeholder="Search CBS…"
                              onChange={(val) =>
                                setEditLines((prev) =>
                                  prev.map((l) =>
                                    l.key === row.key
                                      ? { ...l, cbs_code_id: (val as string) || null }
                                      : l
                                  )
                                )
                              }
                            />
                          ) : (
                            (v && cbsLabelById.get(v)) || '—'
                          ),
                      },
                      {
                        title: 'Category',
                        dataIndex: 'cost_category',
                        width: 130,
                        render: (v: string, row: BudgetLine) =>
                          canEdit ? (
                            <Select
                              size="small"
                              style={{ width: '100%' }}
                              value={v}
                              status={
                                lineIssues.get(row.key)?.includes('Invalid category')
                                  ? 'error'
                                  : undefined
                              }
                              options={COST_CATEGORIES.map((c) => ({ value: c, label: c }))}
                              onChange={(val) =>
                                setEditLines((prev) =>
                                  prev.map((l) =>
                                    l.key === row.key ? { ...l, cost_category: val } : l
                                  )
                                )
                              }
                            />
                          ) : (
                            v
                          ),
                      },
                      {
                        title: 'Qty',
                        dataIndex: 'budgeted_quantity',
                        width: 90,
                        render: (v: number, row: BudgetLine) =>
                          canEdit ? (
                            <InputNumber
                              size="small"
                              style={{ width: '100%' }}
                              value={v}
                              onChange={(val) =>
                                setEditLines((prev) =>
                                  prev.map((l) => {
                                    if (l.key !== row.key) return l;
                                    const qty = Number(val || 0);
                                    const unit = Number(l.budgeted_unit_cost || 0);
                                    return {
                                      ...l,
                                      budgeted_quantity: qty,
                                      budgeted_amount: round2(qty * unit),
                                    };
                                  })
                                )
                              }
                            />
                          ) : (
                            v
                          ),
                      },
                      {
                        title: 'Unit cost',
                        dataIndex: 'budgeted_unit_cost',
                        width: 100,
                        render: (v: number, row: BudgetLine) =>
                          canEdit ? (
                            <InputNumber
                              size="small"
                              style={{ width: '100%' }}
                              value={v}
                              onChange={(val) =>
                                setEditLines((prev) =>
                                  prev.map((l) => {
                                    if (l.key !== row.key) return l;
                                    const unit = Number(val || 0);
                                    const qty = Number(l.budgeted_quantity || 0);
                                    return {
                                      ...l,
                                      budgeted_unit_cost: unit,
                                      budgeted_amount: round2(qty * unit),
                                    };
                                  })
                                )
                              }
                            />
                          ) : (
                            formatMoney(v)
                          ),
                      },
                      {
                        title: 'Amount',
                        dataIndex: 'budgeted_amount',
                        width: 110,
                        align: 'right' as const,
                        render: (v: number, row: BudgetLine) =>
                          canEdit ? (
                            <InputNumber
                              size="small"
                              style={{ width: '100%' }}
                              value={v}
                              status={
                                lineIssues.get(row.key)?.includes('Amount must be > 0')
                                  ? 'error'
                                  : undefined
                              }
                              onChange={(val) =>
                                setEditLines((prev) =>
                                  prev.map((l) =>
                                    l.key === row.key
                                      ? { ...l, budgeted_amount: round2(Number(val || 0)) }
                                      : l
                                  )
                                )
                              }
                            />
                          ) : (
                            formatMoney(v)
                          ),
                      },
                      {
                        title: 'Actual',
                        dataIndex: 'actual_amount',
                        width: 100,
                        align: 'right' as const,
                        render: (v: number) => formatMoney(Number(v || 0)),
                      },
                      {
                        title: 'Committed',
                        dataIndex: 'committed_amount',
                        width: 100,
                        align: 'right' as const,
                        render: (v: number) => formatMoney(Number(v || 0)),
                      },
                      {
                        title: 'Variance',
                        dataIndex: 'variance_amount',
                        width: 100,
                        align: 'right' as const,
                        render: (v: number, row: BudgetLine) => {
                          const amt =
                            v != null
                              ? Number(v)
                              : Number(row.budgeted_amount || 0) -
                                Number(row.actual_amount || 0);
                          return (
                            <Text type={amt < 0 ? 'danger' : undefined}>{formatMoney(amt)}</Text>
                          );
                        },
                      },
                      {
                        title: 'Notes',
                        dataIndex: 'notes',
                        render: (v: string, row: BudgetLine) =>
                          canEdit ? (
                            <Input
                              size="small"
                              value={v}
                              onChange={(e) =>
                                setEditLines((prev) =>
                                  prev.map((l) =>
                                    l.key === row.key ? { ...l, notes: e.target.value } : l
                                  )
                                )
                              }
                            />
                          ) : (
                            v || '—'
                          ),
                      },
                      ...(canEdit
                        ? [
                            {
                              title: '',
                              width: 64,
                              render: (_: unknown, row: BudgetLine) => (
                                <Button
                                  size="small"
                                  danger
                                  type="link"
                                  onClick={() =>
                                    setEditLines((prev) =>
                                      prev.filter((l) => l.key !== row.key)
                                    )
                                  }
                                >
                                  Remove
                                </Button>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />

                  <div style={{ marginTop: 16 }}>
                    <ApprovalPanel
                      entityType={ENTITY_TYPE_PROJECT_BUDGET_REVISION}
                      entityId={effectiveRevId}
                      extraInvalidateQueryKeys={[
                        ['project-budget-revisions', projectId],
                        ['project-budget-revision', projectId, effectiveRevId],
                      ]}
                    />
                  </div>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Space>

      <ProjectBudgetExcelImportWizard
        open={importWizardOpen}
        onClose={() => setImportWizardOpen(false)}
        projectId={projectId}
        revisionId={canEdit ? effectiveRevId : undefined}
        readOnly={false}
        onDownloadTemplate={downloadTemplate}
        onImported={(payload) => {
          qc.invalidateQueries({ queryKey: ['project-budget-revisions', projectId] });
          qc.invalidateQueries({ queryKey: ['project-budget-structure', projectId] });
          if (payload?.revisionId) goToRevision(payload.revisionId);
        }}
      />
    </div>
  );
};

/** Approval deep-link bridge: /prj/budget-revisions/:revisionId → workbench */
export const ProjectBudgetRevisionBridgePage: React.FC = () => {
  const { revisionId = '' } = useParams<{ revisionId: string }>();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['project-budget-revision-bridge', revisionId],
    queryFn: () => projectService.getBudgetRevisionById(revisionId),
    enabled: !!revisionId,
  });

  useEffect(() => {
    if (q.data?.revision) {
      const projectId = String(q.data.revision.project_id);
      navigate(`/prj/projects/${projectId}/budgets?revisionId=${encodeURIComponent(revisionId)}`, {
        replace: true,
      });
    }
  }, [q.data, revisionId, navigate]);

  if (q.isError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Budget revision not found" description={(q.error as Error)?.message} />
      </div>
    );
  }
  return (
    <div style={{ padding: 24 }}>
      <Alert type="info" message="Opening Project Cost Budget…" />
    </div>
  );
};

/** Approval deep-link: /prj/budget-exceedances/:id → project workbench */
export const ProjectBudgetExceedanceBridgePage: React.FC = () => {
  const { exceedanceId = '' } = useParams<{ exceedanceId: string }>();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['project-budget-exceedance-bridge', exceedanceId],
    queryFn: () => projectService.getBudgetExceedanceById(exceedanceId),
    enabled: !!exceedanceId,
  });

  useEffect(() => {
    if (q.data?.project_id) {
      navigate(
        `/prj/projects/${String(q.data.project_id)}/budgets?exceedanceId=${encodeURIComponent(exceedanceId)}`,
        { replace: true }
      );
    }
  }, [q.data, exceedanceId, navigate]);

  if (q.isError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Budget exceedance not found" description={(q.error as Error)?.message} />
      </div>
    );
  }
  return (
    <div style={{ padding: 24 }}>
      <Alert type="info" message="Opening Budget Exceedance…" />
    </div>
  );
};

export default ProjectBudgetWorkbenchPage;
